// "Risk limits" — optional stop-loss / take-profit / trailing-stop applied to each SIMULATED
// trade in the backtest, on top of the exit rule. All-blank = off (default). When set, the
// values also become the suggestedSL / suggestedTP hints in the webhook payload.

import { useBuilderState } from "../../state/BuilderState";
import type { RiskSettingsDto } from "../../api/types";
import { cn } from "@/lib/utils";

type RiskKey = keyof RiskSettingsDto;

const FIELDS: { key: RiskKey; label: string; hint: string }[] = [
  { key: "stopLossPercent", label: "Stop loss", hint: "Close the trade if it moves this % against you from entry." },
  { key: "takeProfitPercent", label: "Take profit", hint: "Close the trade once it moves this % in your favour from entry." },
  { key: "trailingStopPercent", label: "Trailing stop", hint: "Close if price pulls back this % from the best level reached." },
];

export function RiskSection() {
  const { draft, setDraft } = useBuilderState();
  const rs = draft.riskSettings ?? {};

  const setField = (key: RiskKey, raw: string) => {
    const trimmed = raw.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);
    const value = parsed !== null && Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    // Merge against the LATEST draft (not the render-closure) so editing two fields before a
    // re-render doesn't drop the first edit.
    setDraft((prev) => ({
      ...prev,
      riskSettings: { ...(prev.riskSettings ?? {}), [key]: value },
    }));
  };

  const anyOn = FIELDS.some((f) => {
    const v = rs[f.key];
    return typeof v === "number" && v > 0;
  });

  return (
    <div className="rounded-2xl border border-border bg-card p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Risk limits <span className="text-muted-foreground font-normal">· optional</span></h3>
          <p className="text-xs text-muted-foreground mt-0.5">Auto-close a simulated trade at a stop or target.</p>
        </div>
        <span
          className={cn(
            "text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold shrink-0",
            anyOn ? "bg-primary/15 text-primary" : "bg-muted/40 text-muted-foreground/70",
          )}
        >
          {anyOn ? "On" : "Off"}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {FIELDS.map((f) => {
          const v = rs[f.key];
          return (
            <div key={f.key} className="flex flex-col gap-1">
              <label className="text-[11px] font-medium text-muted-foreground">{f.label}</label>
              <div className="relative">
                <input
                  type="number"
                  min={0}
                  step={0.1}
                  placeholder="off"
                  value={typeof v === "number" ? v : ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  className="w-full h-8 rounded-lg border border-border bg-background/60 text-sm font-mono pl-2 pr-6 outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/40"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground/60 pointer-events-none">%</span>
              </div>
              <p className="text-[10px] text-muted-foreground/70 leading-snug">{f.hint}</p>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-muted-foreground/70 leading-snug">
        Leave blank to skip. Percentages are measured from the entry price.
      </p>
    </div>
  );
}
